'use client';

import React from 'react';
import type { PaletteOptions, Shadows } from '@mui/material/styles';
import { alpha, createTheme } from '@mui/material/styles';
import InfoCircleIcon from 'components/vectors/InfoCircleIcon';
// import { getYooptaCustomStyles } from './getYooptaCustomStyles';

interface Emphasis {
  high: string;
  medium: string;
  low: string;
  disabled: string;
}

declare module '@mui/material/styles' {
  interface ThemeOptions {
    elevation?: {
      sm: string;
      lg: string;
      xl: string;
    };
    drawerWidth?: string;
  }

  interface Theme {
    elevation: {
      sm: string;
      lg: string;
      xl: string;
    };
    drawerWidth: string;
  }

  interface PaletteOptions {
    outline?: {
      light: string;
      medium: string;
      inverseMedium: string;
    };
    backdrop?: {
      main: string;
    };
  }

  interface Palette {
    outline: {
      light: string;
      medium: string;
      inverseMedium: string;
    };

    backdrop: {
      main: string;
    };
  }

  interface PaletteColor extends Emphasis {
    second?: string;
    variant?: string;
  }

  interface SimplePaletteColorOptions extends Emphasis {
    second?: string;
    variant?: string;
  }

  interface TypeBackground {
    default: string;
    paper: string;
    warning: string;
    error: string;
    secondaryVariant: string;
    success: string;
  }

  interface TypeAction {
    secondaryHover: string;
    secondaryHoverOpacity: number;
    secondarySelected: string;
    secondarySelectedOpacity: number;
  }

  interface TypeText {
    tertiary: string;
    background: Emphasis;
    surface: Emphasis;
    error: Emphasis;
    warning: Emphasis;
    success: Emphasis;
  }
}
declare module '@mui/material/styles/createTypography' {
  interface FontStyle {
    fontWeightSemibold: React.CSSProperties['fontWeight'];
  }

  interface TypographyOptions {
    caption2?: React.CSSProperties;
    yooptaDisplay?: React.CSSProperties;
    yooptaH1?: React.CSSProperties;
    yooptaH2?: React.CSSProperties;
    yooptaH3?: React.CSSProperties;
    yooptaText?: React.CSSProperties;
  }

  interface Typography {
    caption2: React.CSSProperties;
    fontWeightSemibold: React.CSSProperties['fontWeight'];
    yooptaDisplay: React.CSSProperties;
    yooptaH1: React.CSSProperties;
    yooptaH2: React.CSSProperties;
    yooptaH3: React.CSSProperties;
    yooptaText: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    yooptaDisplay: true;
    yooptaH1: true;
    yooptaH2: true;
    yooptaH3: true;
    yooptaText: true;
    caption2: true;
  }

  interface TypographyClasses {
    yooptaDisplay: string;
    yooptaH1: string;
    yooptaH2: string;
    yooptaH3: string;
    yooptaText: string;
    caption2: string;
  }
}

// Update the Button's color prop options
declare module '@mui/material' {
  interface Color {
    '0': string;
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    secondary: true;
  }

  interface AlertClasses {
    outlinedSecondary: string;
    standardSecondary: string;
    filledSecondary: string;
  }
}
const disabledStyle = ({ bgColor, color, ...rest }: Record<string, string>) => ({
  color,
  // color: 'rgba(255, 255, 255, 0.50)',
  ...(bgColor && {
    backgroundColor: bgColor,
  }),
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.40)',
  },
  ...rest,
});

const elevation = {
  sm: '0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)',
  lg: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
  xl: '0px 5px 75px 0px rgba(16, 24, 43, 0.07)',
};
const shadows = Array(25).fill('none') as Shadows;
shadows[1] = elevation.sm;
shadows[2] = elevation.lg;
shadows[3] = elevation.xl;

const palette: PaletteOptions = {
  primary: {
    main: '#121212',
    contrastText: '#fff',
    variant: '#323232',
    high: '#FFF',
    medium: alpha('#FFF', 0.7),
    disabled: alpha('#FFF', 0.5),
    low: '',
  },
  backdrop: {
    main: 'rgba(0, 0, 0, 0.60)',
  },
  secondary: {
    main: '#FFFFFF',
    contrastText: alpha('#000000', 0.92),
    variant: '#F1F1F1',
    high: alpha('#000000', 0.92),
    medium: alpha('#000000', 0.62),
    low: alpha('#000000', 0.48),
    disabled: alpha('#000000', 0.2),
  },
  grey: {
    '0': '#FFFFFF',
    '100': '#F5F5F5',
    '200': '#F1F1F1',
    '300': '#EDEDED',
    '400': '#E5E5E5',
    '500': '#A1A1A1',
    '600': '#898989',
    '700': '#292929',
    '800': '#121212',
  },
  error: {
    main: '#F44336',
    light: '#FCF3F2',
    contrastText: '#fff',
    high: '#FFF',
    medium: alpha('#FFF', 0.7),
    low: '',
    disabled: alpha('#FFF', 0.5),
  },
  warning: {
    main: '#F79009',
    contrastText: '#fff',
    high: '#FFF',
    medium: alpha('#FFF', 0.7),
    low: '',
    disabled: alpha('#FFF', 0.5),
  },
  success: {
    main: '#4CA86F',
    contrastText: '#fff',
    high: '#FFF',
    medium: alpha('#FFF', 0.7),
    low: '',
    disabled: alpha('#FFF', 0.5),
  },
  action: {
    hover: 'rgba(255, 255, 255, 0.10)',
    hoverOpacity: 0.1,
    secondaryHover: 'rgba(0, 0, 0, 0.06)',
    secondaryHoverOpacity: 0.06,
    secondarySelected: 'rgba(0, 0, 0, 0.04)',
    secondarySelectedOpacity: 0.04,
    disabledOpacity: 0.4,
    disabledBackground: undefined,
    disabled: undefined,
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
    secondaryVariant: '#F1F1F1',
    success: '#EDF6F1',
    warning: '#FFF6E9',
    error: '#FCF3F2',
  },
  divider: 'rgba(0, 0, 0, 0.06)',
  outline: {
    light: 'rgba(0, 0, 0, 0.06)',
    medium: 'rgba(0, 0, 0, 0.08)',
    inverseMedium: 'rgba(255, 255, 255, 0.10)',
  },
  text: {
    primary: '#121212',
    secondary: '#636363',
    tertiary: '#A4A4A4',
    disabled: '#D0D0D0',
    background: {
      high: alpha('#000000', 0.92),
      medium: alpha('#000000', 0.48),
      low: '',
      disabled: alpha('#000000', 0.2),
    },
    surface: {
      high: alpha('#000000', 0.92),
      medium: alpha('#000000', 0.48),
      low: '',
      disabled: alpha('#000000', 0.2),
    },
    error: {
      high: alpha('#FFFFFF', 1),
      medium: alpha('#FFFFFF', 0.7),
      low: '',
      disabled: alpha('#FFFFFF', 0.5),
    },
    warning: {
      high: alpha('#FFFFFF', 1),
      medium: alpha('#FFFFFF', 0.7),
      low: '',
      disabled: alpha('#FFFFFF', 0.5),
    },
    success: {
      high: alpha('#FFFFFF', 1),
      medium: alpha('#FFFFFF', 0.7),
      low: '',
      disabled: alpha('#FFFFFF', 0.5),
    },
  },
};

const defaultTheme = createTheme({
  palette,
  shadows,
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
    allVariants: {
      fontFamily: ['Inter', 'sans-serif'].join(','),
    },
  },
});

export const theme = createTheme({
  elevation,
  shadows,
  drawerWidth: '280px',
  palette,
  typography: (currentPalette) => {
    const fontWeightBold = 700;
    const fontWeightSemibold = 600;
    const fontWeightMedium = 500;
    const fontWeightRegular = 400;

    return {
      fontWeightBold,
      fontWeightSemibold,
      fontWeightMedium,
      fontWeightRegular,
      fontFamily: ['Inter', 'sans-serif'].join(','),
      allVariants: {
        fontFamily: ['Inter', 'sans-serif'].join(','),
        color: currentPalette.text.background.high,
      },
      h1: {
        fontSize: defaultTheme.typography.pxToRem(30),
        fontWeight: fontWeightSemibold,
        lineHeight: '120%',
        letterSpacing: '-0.01em', // -1%
        // mobile
        [defaultTheme.breakpoints.down('sm')]: {
          fontSize: defaultTheme.typography.pxToRem(20),
        },
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: fontWeightSemibold,
        lineHeight: '115%',
        letterSpacing: '-0.01em', // -1%
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: fontWeightMedium,
        lineHeight: '135%',
        letterSpacing: '-0.01em', // -1%
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(16),
        fontWeight: fontWeightRegular,
        lineHeight: '125%',
        letterSpacing: '-0.01em', // -1%
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: fontWeightRegular,
        lineHeight: '140%',
        letterSpacing: '-0.01em', // -1%
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: fontWeightRegular,
        lineHeight: '135%',
        letterSpacing: '-0.01em', // -1%
      },
      caption2: {
        fontSize: defaultTheme.typography.pxToRem(10),
        fontWeight: fontWeightRegular,
        lineHeight: '135%',
        letterSpacing: '-0.2px',
      },
      button: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: fontWeightSemibold,
        lineHeight: '100%',
        letterSpacing: '-0.01em', // -1%
      },
      yooptaDisplay: {
        fontSize: defaultTheme.typography.pxToRem(40),
        fontWeight: fontWeightBold,
        lineHeight: '130%',
        [defaultTheme.breakpoints.down('sm')]: {
          fontSize: defaultTheme.typography.pxToRem(30),
        },
      },
      yooptaH1: {
        fontSize: defaultTheme.typography.pxToRem(40),
        fontWeight: fontWeightSemibold,
        lineHeight: '130%',
        letterSpacing: 'normal',
        [defaultTheme.breakpoints.down('sm')]: {
          fontSize: defaultTheme.typography.pxToRem(26),
        },
      },
      yooptaH2: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: fontWeightSemibold,
        lineHeight: '130%',
        letterSpacing: 'normal',
        [defaultTheme.breakpoints.down('sm')]: {
          fontSize: defaultTheme.typography.pxToRem(22),
        },
      },
      yooptaH3: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: fontWeightSemibold,
        lineHeight: '130%',
        letterSpacing: '-0.2px',
      },
      yooptaText: {
        fontSize: defaultTheme.typography.pxToRem(16),
        fontWeight: fontWeightRegular,
        lineHeight: '150%',
        letterSpacing: '-0.16px',
      },
    };
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (innerTheme) => ({
        'body *': {
          fontFamily: ['Inter', 'sans-serif'].join(','),
        },
        '.text-regular': {
          fontWeight: innerTheme.typography.fontWeightRegular,
        },
        '#__next': {
          // height: '100%',
          width: '100%',
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
        // ...getYooptaCustomStyles(theme),
      }),
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        rounded: {
          borderRadius: '10px',
        },
        outlined: ({ theme }) => ({
          borderColor: theme.palette.outline.medium,
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          caption: 'p',
          yooptaDisplay: 'h1',
          yooptaH1: 'h1',
          yooptaH2: 'h2',
          yooptaH3: 'h3',
          yooptaText: 'p',
        },
      },
      styleOverrides: {
        root: {
          '& strong': {
            fontWeight: 600,
          },
        },
        // yoopta
        yooptaDisplay: ({ theme }) => ({
          '&.padding': {
            padding: theme.spacing('4px', '2px'),
          },
        }),
        yooptaH1: ({ theme }) => ({
          '&.padding': {
            padding: theme.spacing('0px', '2px', '4px'),
          },
        }),
        yooptaH2: ({ theme }) => ({
          '&.padding': {
            padding: theme.spacing('0px', '2px', '4px'),
          },
        }),
        yooptaH3: ({ theme }) => ({
          '&.padding': {
            padding: theme.spacing('0px', '2px', '4px'),
          },
        }),
        yooptaText: ({ theme }) => ({
          '&.padding': {
            padding: theme.spacing('4px', '2px'),
          },
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.color-light': {
            borderColor: theme.palette.outline.light,
            '&::before, &::after': {
              borderColor: theme.palette.outline.light,
            },
          },
          '&.color-medium': {
            borderColor: theme.palette.outline.medium,
            '&::before, &::after': {
              borderColor: theme.palette.outline.medium,
            },
          },
        }),
        wrapper: {
          padding: '0px 20px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'large',
        disableElevation: true,
        disableTouchRipple: true,
        // disableRipple: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          overflow: 'hidden',
          textTransform: 'none',
          transformOrigin: 'center bottom 0px',
          transform: 'matrix(1, 0, 0, 1, 0, 0)',
          transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
            duration: theme.transitions.duration.short,
          }),
          '&:active': {
            transform: 'matrix(0.95, 0, 0, 0.95, 0, 0)',
          },
          '& .MuiTouchRipple-root': {
            borderRadius: 0,
            transition: theme.transitions.create(['background-color'], {
              duration: theme.transitions.duration.short,
            }),
          },
        }),
        sizeSmall: {
          minHeight: '30px',
          padding: '0px 12px',
          borderRadius: '8px',
          '& .MuiButton-endIcon, & .MuiButton-startIcon': {
            // marginTop: '-2px',
            // marginBottom: '-2px',
            '& > svg': {
              fontSize: '16px',
            },
          },
        },
        sizeMedium: {
          minHeight: '36px',
          padding: '0px 16px',
          borderRadius: '10px',
        },
        sizeLarge: {
          minHeight: '40px',
          padding: '0px 20px',
          borderRadius: '10px',
          gap: '6px',
          '& .MuiButton-endIcon, & .MuiButton-startIcon': {
            margin: 0,
            // marginTop: '-3px',
            // marginBottom: '-3px',
            '& > svg': {
              fontSize: '20px',
            },
          },
        },
        containedPrimary: ({ theme }) => ({
          '&:hover, &:active': {
            '& .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.hover,
            },
          },
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
          // '&.Mui-disabled': disabledStyle({ bgColor: palette.primary.main, color: 'rgba(255, 255, 255, 0.50)' }),
        }),
        containedSecondary: ({ theme }) => ({
          boxShadow: theme.shadows[1],

          '&:hover, &:active': {
            backgroundColor: theme.palette.secondary.main,
            borderColor: theme.palette.outline.medium,
            '& .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.secondaryHover,
            },
          },
          '&.Mui-disabled': disabledStyle({
            color: 'rgba(0, 0, 0, 0.20)',
            boxShadow: theme.shadows[1],
          }),
        }),
        containedError: ({ theme }) => ({
          '&:hover, &:active': {
            '& .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.hover,
            },
          },
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          borderColor: theme.palette.outline.medium,
          '&:hover, &:active': {
            backgroundColor: theme.palette.secondary.main,
            borderColor: theme.palette.outline.medium,
            '& .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.secondaryHover,
            },
          },
          '&.Mui-disabled': disabledStyle({ color: 'rgba(0, 0, 0, 0.20)' }),
        }),
        outlinedError: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          borderColor: theme.palette.outline.medium,
          '&:hover, &:active': {
            borderColor: theme.palette.outline.medium,
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
          '&.Mui-disabled': disabledStyle({}),
        }),
        outlinedSizeSmall: {
          // count with border
          padding: '7px 12px',
        },
        outlinedSizeMedium: {
          // count with border
          padding: '9px 15px',
        },
        outlinedSizeLarge: {
          // count with border
          padding: '9px 16px',
        },
        textSizeMedium: {
          fontSize: '15px',
          paddingLeft: '14px',
          paddingRight: '14px',
        },
        textPrimary: {
          '&:hover, &:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
          '&.Mui-disabled': disabledStyle({ color: 'rgba(0, 0, 0, 0.20)' }),
        },
        textError: {
          '&:hover, &:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
        },
        text: {
          '&.link': {
            fontSize: 'inherit',
            padding: 0,
            minWidth: 'unset',
            backgroundColor: 'transparent',
          },
        },
        startIcon: {
          marginLeft: '0px',
          minWidth: 'unset',
          marginRight: '2px',
        },
        endIcon: {
          marginRight: '0px',
          marginLeft: '2px',
          minWidth: 'unset',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableTouchRipple: true,
        // disableRipple: true,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          lineHeight: 1,
          [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
          },
        }),
        input: {
          fontSize: 'inherit',
        },
        adornedStart: {
          '& .MuiInputBase-input': {
            paddingLeft: '0px',
          },
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '16px',
            marginRight: '0px',
          },
        },
        adornedEnd: {
          '& .MuiInputBase-input': {
            paddingRight: '0px',
          },
          '& .MuiInputAdornment-positionEnd': {
            marginLeft: '0px',
            marginRight: '16px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          gap: '6px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
          translate: 'no',
          disableAnimation: true,
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
            '& .MuiInputBase-input': {
              fontSize: 'inherit',
            },
          },
        }),
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
        translate: 'no',
        disableAnimation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.caption,
          fontWeight: theme.typography.fontWeightMedium,
          position: 'relative',
          transform: 'none',
          color: theme.palette.text.background.high,
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
          '&.Mui-focused:not(.Mui-error)': {
            color: theme.palette.text.background.high,
          },
        }),
        shrink: {
          transform: 'none',
        },
      },
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {},
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          padding: 0,
          backgroundColor: theme.palette.grey[200],
          outline: 'none',
          '& .MuiOutlinedInput-input': {
            '&::placeholder': {
              color: theme.palette.text.background.medium,
              opacity: 1,
            },
          },
          '&:hover:not(.Mui-error)': {
            background: `linear-gradient(0deg, ${theme.palette.action.secondaryHover} 0%, ${theme.palette.action.secondaryHover} 100%), ${theme.palette.grey[200]}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.grey[200],
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
            },
            '&:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
          '&.Mui-disabled': {
            color: theme.palette.text.background.disabled,
          },
        }),
        notchedOutline: {
          top: '0px',
          // border: '0px',
          borderColor: 'transparent',
          '& legend': {
            display: 'none',
          },
        },
        input: ({ theme }) => ({
          padding: theme.spacing(1.25, 2),
          height: 'unset',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.14px',
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: '16px',
          },
          '&.MuiSelect-select': {
            minHeight: 0,
          },
        }),
        inputSizeSmall: {
          padding: '7px 12px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {},
        inputRoot: {
          padding: 0,
        },
        input: ({ theme }) => ({
          padding: `${theme.spacing(1.25, 2)} !important`,
        }),
        paper: ({ theme }) => ({
          marginTop: '6px',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.outline.light}`,
          boxShadow: theme.shadows[1],
        }),
        listbox: {
          padding: '6px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.caption,
          margin: '0px',
          letterSpacing: '-0.02em', // -2%
          color: theme.palette.text.background.medium,
          '& strong': {
            fontWeight: 500,
          },
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
          '&.Mui-disabled': {
            color: theme.palette.text.background.medium,
          },
        }),
      },
    },
    MuiList: {
      defaultProps: {
        disablePadding: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: '2px',
          [theme.breakpoints.down('sm')]: {
            gap: 0,
          },
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0px 8px',
          ...theme.typography.body2,
          color: theme.palette.text.surface.medium,
          fontWeight: theme.typography.fontWeightMedium,
          marginBottom: '4px',
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          flexGrow: 0,
          borderRadius: '6px',
          gap: '8px',
          backgroundColor: theme.palette.secondary.main,
          [theme.breakpoints.down('sm')]: {
            gap: '12px',
          },
        }),
        padding: ({ theme }) => ({
          paddingTop: '6px',
          paddingBottom: '6px',
          [theme.breakpoints.down('sm')]: {
            paddingTop: '8px',
            paddingBottom: '8px',
          },
        }),
        gutters: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          flexGrow: 0,
          borderRadius: '6px',
          padding: '5px 6px',
          gap: '8px',
          backgroundColor: theme.palette.secondary.main,
          '& .MuiListItemIcon-root > .MuiSvgIcon-root': {
            fontSize: '20px',
          },
          [theme.breakpoints.down('sm')]: {
            padding: '8px',
            gap: '12px',
            '& .MuiListItemIcon-root': {
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
              },
              '& .MuiAvatar-root': {
                width: '24px',
                height: '24px',
              },
            },
          },
          '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            '& > .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.secondaryHover,
            },
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
              '& > .MuiTouchRipple-root': {
                backgroundColor: theme.palette.action.secondaryHover,
              },
            },
            '& .MuiListItemIcon-root > .MuiSvgIcon-root': {
              color: theme.palette.text.background.high,
            },
            '& > .MuiTouchRipple-root': {
              backgroundColor: theme.palette.action.secondarySelected,
            },
          },
        }),
      },
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: {
          noWrap: true,
          variant: 'body2',
          fontWeight: 'medium',
        },
        secondaryTypographyProps: {
          variant: 'caption',
          color: 'text.background.medium',
        },
      },
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiListItemSecondaryAction: {
      defaultProps: {
        onClick: (e) => e.stopPropagation(),
      },
      styleOverrides: {
        root: {
          display: 'flex',
          position: 'relative',
          transform: 'none',
          inset: '0px',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 0,
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
            [theme.breakpoints.down('sm')]: {
              fontSize: '24px',
            },
          },
          '& .MuiAvatar-root': {
            width: '20px',
            height: '20px',
            [theme.breakpoints.down('sm')]: {
              width: '24px',
              height: '24px',
            },
          },
        }),
        alignItemsFlexStart: {
          marginTop: '0px',
        },
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 0,
          '& .MuiAvatar-root': {
            width: '20px',
            height: '20px',
            [theme.breakpoints.down('sm')]: {
              width: '24px',
              height: '24px',
            },
          },
        }),
      },
    },

    MuiMenu: {
      defaultProps: {
        disableAutoFocusItem: true,
        elevation: 3,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      },
      styleOverrides: {
        root: {},
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
        }),
        list: {
          padding: '6px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: '4px',
        },
      },
    },
    MuiMenuList: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: '4px',
        },
        padding: {
          padding: '6px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 'unset',
          // minWidth: '188px',
          borderRadius: '6px',
          padding: '5px 10px',
          backgroundColor: theme.palette.secondary.main,
          gap: '8px',
          '& .MuiListItemIcon-root': {
            minWidth: 0,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.secondaryHover,
          },
          '&:active, &:focus': {
            backgroundColor: theme.palette.action.secondaryHover,
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.secondarySelected,
          },
          '& + .MuiDivider-root': {
            margin: '0px -6px',
          },
        }),
      },
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: false,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: '34px',
          '&.contained': {
            '& .MuiTabs-indicator': {
              height: '100%',
              backgroundColor: theme.palette.action.secondarySelected,
              borderRadius: '10px',
            },
            '& .MuiTab-root': {
              padding: '7px 12px',
              minHeight: '34px',
              minWidth: 'unset',
              borderRadius: '10px',
              transition: theme.transitions.create(['color', 'background-color'], {
                duration: theme.transitions.duration.short,
              }),
              '&:hover': {
                color: theme.palette.text.background.high,
              },
              '&.Mui-selected': {
                color: theme.palette.text.background.high,
              },
            },
          },
        }),
        flexContainer: {
          gap: '10px',
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightMedium,
          color: theme.palette.text.surface.medium,
          textTransform: 'none',
          minHeight: '37px',
          padding: '12.21px 2px',
          minWidth: 'unset',
        }),
      },
    },
    MuiIconButton: {
      defaultProps: {
        color: 'primary',
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
          '& .MuiSvgIcon-root': {
            fontSize: 'inherit',
          },
        }),
        sizeSmall: {
          padding: '0',
          borderRadius: '6px',
          fontSize: '20px',
          '&.MuiIconButton-edgeStart': {
            marginLeft: '0px',
          },
          '&.MuiIconButton-edgeEnd': {
            marginRight: '0px',
          },
        },
        sizeMedium: {
          padding: '4px',
          borderRadius: '8px',
          fontSize: '24px',
          '&.MuiIconButton-edgeStart': {
            marginLeft: '-4px',
          },
          '&.MuiIconButton-edgeEnd': {
            marginRight: '-4px',
          },
        },
        sizeLarge: {
          padding: '6px',
          borderRadius: '8px',
          fontSize: '28px',
          '&.MuiIconButton-edgeStart': {
            marginLeft: '-6px',
          },
          '&.MuiIconButton-edgeEnd': {
            marginRight: '-6px',
          },
        },
        colorPrimary: ({ theme }) => ({
          color: theme.palette.secondary.low,
          '&:hover, &:focus, &:active': {
            color: theme.palette.secondary.medium,
            backgroundColor: theme.palette.action.secondaryHover,
          },
        }),
        colorSecondary: ({ theme }) => ({
          color: theme.palette.secondary.high,
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.action.secondaryHover,
          },
        }),
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'primary',
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '0px',
          width: 'unset',
          height: 'unset',
        },
        switchBase: ({ theme }) => ({
          padding: '2px',
          background: 'transparent',
          '&:hover, &:focus': {
            background: 'transparent',
            '& + .MuiSwitch-track::before': {
              backgroundColor: theme.palette.action.secondaryHover,
            },
          },
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': {
              opacity: 1,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: '#fff',
            },
            '&:hover, &:focus': {
              background: 'transparent',
              '& + .MuiSwitch-track::before': {
                backgroundColor: theme.palette.action.hover,
              },
            },
          },
          '&.Mui-disabled': {
            '& .MuiSwitch-thumb': {
              backgroundColor: '#fff',
            },
            '& + .MuiSwitch-track': {
              opacity: theme.palette.action.disabledOpacity,
              '&::before': {
                backgroundColor: 'transparent',
              },
            },
          },
        }),
        thumb: {
          // zIndex: 1,
          boxShadow: 'none',
          filter:
            'drop-shadow(0px 1px 2px rgba(16, 24, 40, 0.06)) drop-shadow(0px 1px 3px rgba(16, 24, 40, 0.10))',
        },
        track: ({ theme }) => ({
          opacity: 1,
          position: 'relative',
          borderRadius: theme.spacing(2),
          height: theme.spacing(3),
          width: theme.spacing(5),
          background: theme.palette.grey[200],
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            width: theme.spacing(5),
            borderRadius: theme.spacing(2),
            transition: theme.transitions.create('background-color'),
          },
        }),
        colorPrimary: ({ theme }) => ({
          '&.Mui-checked': {
            '& .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.backdrop.main,
        }),
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          '&:not(.MuiDialog-paperFullScreen)': {
            width: 'calc(100% - 32px)',
            border: `1px solid ${theme.palette.divider}`,
            margin: '16px',
            maxHeight: 'calc(100% - 32px)',

            '.MuiDialogTitle-root + .MuiDialogContent-root': {
              paddingTop: theme.spacing(3),
            },

            [theme.breakpoints.down('sm')]: {
              '& .MuiDialogTitle-root': {
                padding: theme.spacing(3, 3, 0),
                '+ .MuiDialogContent-root': {
                  paddingTop: theme.spacing('20px'),
                },
              },
              '& .MuiDialogContent-root': {
                padding: theme.spacing('20px', 3, 3),
              },
            },
          },
        }),

        paperWidthXs: {
          '&:not(.MuiDialog-paperFullScreen)': {
            maxWidth: '360px',
          },
        },
        paperWidthSm: {
          '&:not(.MuiDialog-paperFullScreen)': {
            maxWidth: '480px',
          },
        },
        paperWidthMd: {
          '&:not(.MuiDialog-paperFullScreen)': {
            maxWidth: '600px',
          },
        },
        paperWidthLg: {
          '&:not(.MuiDialog-paperFullScreen)': {
            maxWidth: '700px',
          },
        },
        paperWidthXl: {
          '&:not(.MuiDialog-paperFullScreen)': {
            maxWidth: '1000px',
          },
        },
        paperFullScreen: ({ theme }) => ({
          '& .MuiDialogTitle-root': {
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing('9px', 1),
              borderBottom: `1px solid ${theme.palette.outline.light}`,
              '+ .MuiDialogContent-root': {
                paddingTop: theme.spacing(2),
              },
            },
          },
          '& .MuiDialogContent-root': {
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(2, 2, 3, 2),
            },
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: () => ({
          // '+ .MuiDialogContent-root': {
          //   paddingTop: theme.spacing(3),
          // },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(4),
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          padding: theme.spacing(1),
        }),
      },
    },
    MuiSwipeableDrawer: {
      defaultProps: {
        disableSwipeToOpen: true,
        anchor: 'bottom',
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'none',
        color: 'primary',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          width: 'fit-content',
          '&.MuiTypography-button': {
            ...theme.typography.button,
            cursor: 'pointer',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            textTransform: 'none',
            textDecoration: 'none',
            '&.primary': {
              color: theme.palette.primary.main,
              '&:hover, &:active, &:focus': {
                color: theme.palette.primary.variant,
              },
            },
          },
        }),
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          strokeLinecap: 'round',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          color: theme.palette.primary.contrastText,
        }),
        colorAction: ({ theme }) => ({
          color: theme.palette.text.background.medium,
        }),
        colorSecondary: ({ theme }) => ({
          color: theme.palette.text.background.high,
        }),
        colorDisabled: ({ theme }) => ({
          color: theme.palette.text.surface.disabled,
        }),
      },
    },
    MuiAvatar: {
      defaultProps: {
        variant: 'circular',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 600,
          backgroundColor: theme.palette.secondary.variant,
          color: theme.palette.text.background.medium,
          width: '40px',
          height: '40px',
          fontSize: '16px',
          textTransform: 'uppercase',
          '&.xs2': {
            width: '20px',
            height: '20px',
            fontSize: '8px',
          },
          '&.xs': {
            width: '28px',
            height: '28px',
            fontSize: '12px',
          },
          '&.sm': {
            width: '40px',
            height: '40px',
            fontSize: '16px',
          },
          '&.md': {
            width: '50px',
            height: '50px',
            fontSize: '20px',
          },
          '&.lg': {
            width: '70px',
            height: '70px',
            fontSize: '24px',
          },
          '&.xl': {
            width: '100px',
            height: '100px',
            fontSize: '30px',
          },
          '&.xl2': {
            width: '150px',
            height: '150px',
            fontSize: '42px',
          },
          '&.onboardingSize': {
            width: '60px',
            height: '60px',
            fontSize: '32px',
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
        disableInteractive: true,
      },
      styleOverrides: {
        tooltipPlacementBottom: {
          marginTop: '4px !important',
        },
        tooltip: ({ theme }) => ({
          padding: '4px 8px',
          backgroundColor: theme.palette.primary.main,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          '&.large': {
            padding: '8px 10px',
          },
          '& > .MuiTypography-root': {
            color: theme.palette.primary.contrastText,
          },
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'secondary',
      },
    },
    MuiToolbar: {
      defaultProps: {
        variant: 'dense',
        disableGutters: true,
      },
      styleOverrides: {
        dense: ({ theme }) => ({
          minHeight: '42px',
          padding: '6px 16px',
          [theme.breakpoints.down('md')]: {
            padding: '3px 8px',
          },
        }),
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 'unset',
          width: '100%',
          position: 'fixed',
          bottom: 0,
          left: 0,
          zIndex: theme.zIndex.appBar,
          padding: '6px 16px 18px',
          justifyContent: 'space-around',
          borderTop: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 'unset',
          maxWidth: 'unset',
          padding: '6px',
          borderRadius: '8px',
          flex: 'unset',
          color: theme.palette.text.background.medium,
          '&:hover': {
            backgroundColor: theme.palette.action.secondaryHover,
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.secondarySelected,
            color: theme.palette.text.background.high,
            '& .MuiBottomNavigationAction-wrapper': {
              color: theme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.caption,
          fontWeight: theme.typography.fontWeightMedium,
          backgroundColor: theme.palette.secondary.variant,
          borderColor: theme.palette.outline.medium,
          '& .MuiChip-label': {
            color: theme.palette.secondary.low,
          },
        }),
        label: ({ theme }) => ({
          ...theme.typography.caption,
          fontWeight: theme.typography.fontWeightMedium,
        }),
        colorSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.success.main,
          borderColor: theme.palette.action.hover,
          '& .MuiChip-label': {
            color: theme.palette.success.contrastText,
          },
          '&.MuiChip-outlined': {
            backgroundColor: theme.palette.background.success,
            '& .MuiChip-label': {
              color: theme.palette.success.main,
            },
          },
        }),
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.action.hover,
          '& .MuiChip-label': {
            color: theme.palette.primary.contrastText,
          },
          '&.MuiChip-outlined': {
            backgroundColor: theme.palette.background.paper,
            '& .MuiChip-label': {
              color: theme.palette.primary.main,
            },
          },
        }),
        colorError: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          borderColor: theme.palette.action.hover,
          '& .MuiChip-label': {
            color: theme.palette.error.contrastText,
          },
          '&.MuiChip-outlined': {
            backgroundColor: theme.palette.background.error,
            '& .MuiChip-label': {
              color: theme.palette.error.main,
            },
          },
        }),
        colorWarning: ({ theme }) => ({
          backgroundColor: theme.palette.warning.main,
          borderColor: theme.palette.action.hover,
          '& .MuiChip-label': {
            color: theme.palette.warning.contrastText,
          },
          '&.MuiChip-outlined': {
            backgroundColor: theme.palette.background.warning,
            '& .MuiChip-label': {
              color: theme.palette.warning.main,
            },
          },
        }),
        colorInfo: ({ theme }) => ({
          backgroundColor: theme.palette.info.main,
          borderColor: theme.palette.action.hover,
          '& .MuiChip-label': {
            color: theme.palette.info.contrastText,
          },
        }),
        sizeSmall: ({ theme }) => ({
          height: 'unset',
          borderRadius: '4px',
          padding: '0.5px 4px 1.5px',
          '& .MuiChip-label': {
            ...theme.typography.caption2,
            padding: '0px',
          },
        }),
        sizeMedium: {
          height: 'unset',
          borderRadius: '6px',
          padding: '3px 8px',
          lineHeight: '1',
          '&.MuiChip-outlined': {
            padding: '2px 7px',
          },
        },
        labelMedium: {
          padding: '0px',
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: <InfoCircleIcon fontSize="small" color="error" />,
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '10px 12px',
          alignItems: 'center',
          rowGap: '12px',
          flexWrap: 'wrap',
          '&.small': {
            padding: '8px',
            '& .MuiAlert-message': {
              ...theme.typography.caption,
            },
            '& .MuiAlert-icon': {
              fontSize: '18px',
            },
          },
        }),
        icon: {
          margin: 0,
          marginRight: '6px',
          padding: 0,
        },
        message: ({ theme }) => ({
          ...theme.typography.body2,
          marginRight: 'auto',
          padding: 0,
        }),
        action: {
          justifySelf: 'flex-end',
          margin: '0px',
          padding: '0px',
          marginLeft: '8px',
        },
        standardSecondary: ({ theme }) => ({
          ...theme.typography.caption,
          backgroundColor: theme.palette.secondary.variant,
          color: theme.palette.secondary.medium,
          '& > .MuiAlert-icon': {
            color: theme.palette.secondary.low,
          },
        }),
        standardError: ({ theme }) => ({
          ...theme.typography.caption,
          backgroundColor: theme.palette.error.light,
          color: theme.palette.secondary.high,
          '& > .MuiAlert-icon': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '&.relative': {
            position: 'relative',
            inset: 0,
            '& .MuiSnackbarContent-root': {
              borderRadius: '0px',
            },
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: theme.shadows[2],
          backgroundColor: theme.palette.primary.main,
          padding: '10px',
          borderRadius: '6px',
          flexGrow: 'initial',
          [theme.breakpoints.down('sm')]: {
            padding: '10px 16px 10px 14px',
          },
          [theme.breakpoints.up('sm')]: {
            minWidth: 'unset',
          },
        }),
        message: {
          width: '100%',
          padding: '0px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '6px',
          '&.relative': {
            position: 'relative',
            inset: 0,
            borderRadius: '0px',
          },
        },
        action: {
          margin: '0px',
          padding: '0px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:last-of-type': {
            '& .MuiTableCell-body': {
              borderBottom: 'none',
            },
          },
          '&.MuiTableRow-hover': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.secondaryHover,
            },
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2,
          borderColor: theme.palette.divider,
          padding: '0px 20px',
          '&:first-of-type': {
            paddingLeft: '8px',
          },
          '&:last-of-type': {
            paddingRight: '8px',
          },
        }),
        head: ({ theme }) => ({
          border: 0,
          color: theme.palette.text.background.medium,
        }),
        body: ({ theme }) => ({
          height: '64px',
          padding: '8px 20px',
          fontWeight: theme.typography.fontWeightMedium,
          '&:first-of-type': {
            paddingLeft: '8px',
          },
          '&:last-of-type': {
            paddingRight: '8px',
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableFocusRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '6px',
          // color
          padding: '0px',
          '&:hover, &:focus': {
            backgroundColor: theme.palette.action.secondaryHover,
          },
          '&.Mui-checked:hover, &.Mui-checked:focus': {
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: theme.palette.action.hover,
            },
          },
          '& .MuiTouchRipple-root': {},
        }),
      },
    },
    MuiMobileStepper: {
      styleOverrides: {
        dots: {
          gap: '16px',
        },
        dot: ({ theme }) => ({
          width: '6px',
          height: '6px',
          margin: '0px',
          '&:not(.MuiMobileStepper-dotActive)': {
            backgroundColor: theme.palette.secondary.disabled,
          },
        }),
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
});
